import { useState, useEffect, useMemo } from 'react';

import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import MemberEngagementView from './MemberEngagementView';

import LoadingAnimation from '../../../Animation/NewLoading.json';


export default function MemberEngagementBox(props) {
  const {
    calcDateText,
    startDate,
    endDate,
    engagementReserves,
    allMembers,
    isOpenPartnerGymSales,
    isEngagementLoading,
    setIsEngagementLoading,
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [allAgeGroups, setAllAgeGroups] = useState([]);
  const [engagementStates, setEngagementStates] = useState([]);
  const [engagementTotalCount, setEngagementTotalCount] = useState(0);
  const [engagementMaxCount, setEngagementMaxCount] = useState(0);
  const [engagementMaxText, setEngagementMaxText] = useState('');

  // 회원별 출생연도 + 산정기간 내 유효 회원권 사전 파싱 (유효회원만 포함) //
  const parsedAllMembers = useMemo(() => {
    if (!allMembers || !startDate || !endDate) return [];

    return allMembers
      .filter(m => m.birthDay && Array.isArray(m.usages))
      .map(m => ({
        memberId: m.memberId,
        birthYear: parseInt(m.birthDay.slice(0, 4)),
        // 산정기간과 overlap 되는 회원권만 등록 (memShipId/memShipName 누락 방어 포함)
        memberships: m.usages
          .filter(u =>
            u.memberShipId &&
            u.memberShipName &&
            moment(u.startDate).isSameOrBefore(endDate, 'day') &&
            moment(u.endDate).isSameOrAfter(startDate, 'day')
          )
          .map(u => ({
            membershipId: u.memberShipId,
            membershipName: u.memberShipName,
          })),
      }))
      // 유효 회원권이 1개 이상인 회원만 유지 (= 유효회원)
      .filter(m => m.memberships.length > 0);
  }, [allMembers, startDate, endDate]);

  // 연령별 이용자 분석 데이터 가공 //
  useEffect(() => {
    const year = parseInt(moment().format('YYYY'));

    const engagementStateList = [
      { text: t('statistics.engagement.ageUnder10'), startYear: year - 8, endYear: year, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age10s'), startYear: year - 18, endYear: year - 9, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age20s'), startYear: year - 28, endYear: year - 19, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age30s'), startYear: year - 38, endYear: year - 29, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age40s'), startYear: year - 48, endYear: year - 39, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age50s'), startYear: year - 58, endYear: year - 49, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age60s'), startYear: year - 68, endYear: year - 59, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age70s'), startYear: year - 78, endYear: year - 69, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age80s'), startYear: year - 88, endYear: year - 79, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
      { text: t('statistics.engagement.age90s'), startYear: year - 98, endYear: year - 89, totalCount: 0, reservedSum: 0, userIds: new Set(), usagesMap: new Map() },
    ];

    // 출생연도 → 연령대 인덱스 (구간을 벗어나면 -1) //
    function getAgeGroupIndex(birthYear) {
      const age = year - birthYear;

      if (age < 0 || age > 98) return -1;

      return Math.floor((age + 1) / 10);
    }

    // Phase 0: 보유 회원권 사전 등록(count: 0) + 보유 고유 회원 집계 //
    for (const member of parsedAllMembers) {
      const idx = getAgeGroupIndex(member.birthYear);

      if (idx === -1) continue;

      const group = engagementStateList[idx];
      group.userIds.add(member.memberId);

      for (const m of member.memberships) {
        if (!group.usagesMap.has(m.membershipId)) {
          group.usagesMap.set(m.membershipId, {
            membershipId: m.membershipId,
            membershipName: m.membershipName,
            count: 0
          });
        }
      }
    }

    // Phase 1: 사용 실적(reserve) 누적 //
    for (const reserve of engagementReserves) {
      const idx = getAgeGroupIndex(parseInt(reserve.birthDay.slice(0, 4)));

      if (idx === -1) continue;

      const group = engagementStateList[idx];
      group.reservedSum += 1;

      const existing = group.usagesMap.get(reserve.membershipId);

      if (existing) {
        existing.count += 1;
      }
      else {
        group.usagesMap.set(reserve.membershipId, {
          membershipId: reserve.membershipId,
          membershipName: reserve.membershipName,
          count: 1
        });
      }
    }

    let grandTotal = 0;
    let maxText = t('statistics.engagement.ageUnder10');
    let maxCount = 0;

    // Map → 정렬된 배열 변환 + 집계 //
    for (const group of engagementStateList) {
      group.usages = Array.from(group.usagesMap.values())
        .sort((a, b) => b.count - a.count || a.membershipId - b.membershipId);
      group.totalCount = group.userIds.size;
      grandTotal += group.totalCount;

      if (group.totalCount > maxCount) {
        maxText = group.text;
        maxCount = group.totalCount;
      }
    }

    setAllAgeGroups(engagementStateList.slice(0, 9));

    const finalEngagementStates = engagementStateList.filter(group => group.usages.length > 0);

    setEngagementStates(finalEngagementStates);
    setEngagementTotalCount(grandTotal);
    setEngagementMaxCount(maxCount);
    setEngagementMaxText(maxText);
    setIsEngagementLoading(false);
  }, [engagementReserves, parsedAllMembers]);

  return (
    <TopLevelWrapper $isDark={isDark}>
      {
        isEngagementLoading ?
          <AnimationBox>
            <LottieWrapper>
              <Lottie animationData={LoadingAnimation} loop={true} />
            </LottieWrapper>
          </AnimationBox>
          :
          isOpenPartnerGymSales ?
            <ContentWrapper>
              <TitleRow>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>
                  {calcDateText()}
                </Text>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>
                  {t('statistics.engagement.ageAnalysis')}
                </Text>
              </TitleRow>
              <MemberEngagementView
                ageGroups={allAgeGroups.map(group => ({ ...group, label: group.text }))}
                engagementStates={engagementStates.map(group => ({ ...group, label: group.text }))}
                engagementTotalCount={engagementTotalCount}
                engagementMaxCount={engagementMaxCount}
                maxLabel={engagementMaxText} />
            </ContentWrapper>
            :
            <NotOpenWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={15} color={theme.subText}>{t('statistics.partnerGymPrivate')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={15} color={theme.subText}>{t('statistics.partnerGymPrivateDesc')}</Text>
            </NotOpenWrapper>
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  margin: 39.5px 0;
  padding: 20px 40px;

  width: 100%;
  max-width: 960px;

  gap: 10px;

  border-radius: 20px;
  background-color: ${props => props.theme.subBackground};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;

  width: 100%;
`;

const AnimationBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 300px;
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 160px;
  height: 160px;
`;

const NotOpenWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  padding: 40px 0;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  cursor: default;
`;